import firebase from "firebase";
import { firestore } from "../firebase/firebase";
import { AssignedPlaceOT } from "../models/OT/Place";
import { ChecklistOT } from "../models/OT/Checklist";
import { SheetOT } from "../models/OT/Sheet";

/**
 * Calcula items pendientes para un espacio específico
 * Basado en README_LOGICA_ICONOS_CHECKLIST.md
 */
export const calculatePendingItemsForEspacio = async (
  espacioRef: firebase.firestore.DocumentReference
): Promise<{ hasPending: boolean; count: number }> => {
  console.log(`[PendingCalculator] 🔍 Calculando items pendientes para espacio: ${espacioRef.path}`);

  try {
    // 1. Verificar si hay respuestas con Estado="En Proceso"
    console.log(`[PendingCalculator] 🔎 Paso 1: Buscando respuestas con Estado="En Proceso"`);
    const inProcessQuery = espacioRef
      .collection("RespuestasOT")
      .where("Estado", "==", "En Proceso")
      .limit(1);

    const inProcessSnapshot = await inProcessQuery.get();
    console.log(`[PendingCalculator] 📊 Respuestas "En Proceso" encontradas: ${inProcessSnapshot.size}`);

    if (!inProcessSnapshot.empty) {
      console.log(`[PendingCalculator] ✅ Encontrado Estado="En Proceso" en espacio: ${espacioRef.path}`);
      return { hasPending: true, count: inProcessSnapshot.size };
    }

    // 2. Verificar respuestas anidadas con Estado="Pendiente"
    console.log(`[PendingCalculator] 🔎 Paso 2: Analizando respuestas anidadas con Estado="Pendiente"`);
    const allResponsesSnapshot = await espacioRef.collection("RespuestasOT").get();
    console.log(`[PendingCalculator] 📊 Total de documentos RespuestasOT: ${allResponsesSnapshot.size}`);
    let pendingCount = 0;

    for (const responseDoc of allResponsesSnapshot.docs) {
      const response = responseDoc.data() as SheetOT;
      console.log(`[PendingCalculator] 🔍 Analizando documento: ${responseDoc.id}, Estado: ${response.Estado}, Revisores: ${response.Revisores?.length || 0}`);

      // Evitar falsos positivos: solo considerar respuestas anidadas si la hoja está "En Proceso"
      if (response.Estado !== "En Proceso") {
        console.log(`[PendingCalculator] ⏭️ Omitiendo documento ${responseDoc.id} con Estado=${response.Estado}`);
        continue;
      }

      // Buscar en la estructura anidada: Revisores[] -> Categorias[] -> Preguntas[] -> Respuestas[]
      let documentPendingCount = 0;
      for (const revisor of response.Revisores || []) {
        for (const categoria of revisor.Categorias || []) {
          for (const pregunta of categoria.Preguntas || []) {
            for (const respuesta of pregunta.Respuestas || []) {
              if (respuesta && respuesta.Estado === "Pendiente") {
                pendingCount++;
                documentPendingCount++;
                console.log(`[PendingCalculator] 🔍 ENCONTRADO! Respuesta pendiente en "${pregunta.Pregunta}" - Estado: ${respuesta.Estado}`);
              }
            }
          }
        }
      }

      if (documentPendingCount > 0) {
        console.log(`[PendingCalculator] 📊 Documento ${responseDoc.id}: ${documentPendingCount} respuestas pendientes`);
      } else {
        console.log(`[PendingCalculator] ✅ Documento ${responseDoc.id}: Sin respuestas pendientes`);
      }
    }

    const hasPending = pendingCount > 0;
    console.log(`[PendingCalculator] 🎯 RESULTADO FINAL PARA ESPACIO: ${pendingCount} items pendientes, hasPending: ${hasPending}`);

    return { hasPending, count: pendingCount };
  } catch (error) {
    console.error(`[PendingCalculator] Error calculando items pendientes para espacio ${espacioRef.path}:`, error);
    return { hasPending: false, count: 0 };
  }
};

/**
 * Calcula items pendientes para un checklist completo
 * Basado en README_LOGICA_ICONOS_CHECKLIST.md
 */
export const calculatePendingItemsForChecklist = async (
  checklistRef: firebase.firestore.DocumentReference
): Promise<{ hasPending: boolean; count: number }> => {
  console.log(`[PendingCalculator] 🔍 Calculando items pendientes para checklist: ${checklistRef.path}`);

  try {
    let totalPendingCount = 0;
    let hasAnyPending = false;

    // Obtener todos los espacios asignados del checklist
    console.log(`[PendingCalculator] 🔎 Buscando espacios en: ${checklistRef.path}/EspaciosAsignadosOT`);
    const espaciosSnapshot = await checklistRef.collection("EspaciosAsignadosOT").get();
    console.log(`[PendingCalculator] 📊 Encontrados ${espaciosSnapshot.size} espacios asignados`);

    for (const espacioDoc of espaciosSnapshot.docs) {
      console.log(`[PendingCalculator] 🏠 Procesando espacio: ${espacioDoc.id} (ruta: ${espacioDoc.ref.path})`);
      const espacioResult = await calculatePendingItemsForEspacio(espacioDoc.ref);
      console.log(`[PendingCalculator] 📊 Espacio ${espacioDoc.id}: ${espacioResult.count} items pendientes`);

      if (espacioResult.hasPending) {
        hasAnyPending = true;
        totalPendingCount += espacioResult.count;
      }
    }

    console.log(`[PendingCalculator] 🎯 RESULTADO CHECKLIST: ${totalPendingCount} items pendientes totales, hasPending: ${hasAnyPending}`);

    return { hasPending: hasAnyPending, count: totalPendingCount };
  } catch (error) {
    console.error(`[PendingCalculator] Error calculando items pendientes para checklist ${checklistRef.path}:`, error);
    return { hasPending: false, count: 0 };
  }
};

/**
 * Actualiza los campos de items pendientes después de un cambio
 * Basado en README_LOGICA_ICONOS_CHECKLIST.md
 */
export const updatePendingStatusAfterChange = async (
  empresaId: string,
  projectId: string,
  checklistId: string,
  espacioId: string
) => {
  console.log(`[PendingCalculator] Actualizando estado pendiente para checklist ${checklistId} después de cambio en espacio ${espacioId}`);
  console.log(`[PendingCalculator] IDs recibidos - empresa: ${empresaId}, proyecto: ${projectId}, checklist: ${checklistId}, espacio: ${espacioId}`);

  try {
    // Referencia al DOCUMENTO específico del ChecklistsOT (inicial)
    const initialChecklistRef = firestore
      .collection('Empresas').doc(empresaId)
      .collection('ProyectosOT').doc(projectId)
      .collection('ChecklistsOT').doc(checklistId);

    let effectiveChecklistRef = initialChecklistRef;

    console.log(`[PendingCalculator] 🎯 Ruta del documento checklist (inicial): ${initialChecklistRef.path}`);

    // 1. Verificar que el documento del checklist existe
    console.log(`[PendingCalculator] 🔍 Verificando existencia del checklist...`);
    const initialChecklistDoc = await initialChecklistRef.get();

    if (!initialChecklistDoc.exists) {
      console.warn(`[PendingCalculator] ❌ El checklist ${checklistId} no existe en la base de datos.`);
      console.warn(`[PendingCalculator] 📁 Ruta completa: ${initialChecklistRef.path}`);

      // Intentar resolver el checklist correcto buscando el espacio en todas las checklists del proyecto
      console.log(`[PendingCalculator] 🔎 Intentando resolver checklist por espacio ${espacioId} en proyecto ${projectId}...`);
      const projectRef = firestore
        .collection('Empresas').doc(empresaId)
        .collection('ProyectosOT').doc(projectId);

      const allChecklistsSnap = await projectRef.collection('ChecklistsOT').get();
      console.log(`[PendingCalculator] 📊 Checklists en proyecto: ${allChecklistsSnap.size}`);

      for (const chkDoc of allChecklistsSnap.docs) {
        const possibleEspacioRef = chkDoc.ref.collection('EspaciosAsignadosOT').doc(espacioId);
        const possibleEspacioDoc = await possibleEspacioRef.get();
        if (possibleEspacioDoc.exists) {
          effectiveChecklistRef = chkDoc.ref;
          console.log(`[PendingCalculator] ✅ Checklist resuelto por espacio: ${chkDoc.id} (ruta: ${chkDoc.ref.path})`);
          break;
        }
      }

      if (effectiveChecklistRef === initialChecklistRef) {
        console.warn(`[PendingCalculator] ⚠️ No se pudo resolver un checklist que contenga el espacio ${espacioId} dentro del proyecto ${projectId}. Intentando fallback global por collectionGroup...`);

        try {
          // Fallback: buscar el espacio por ID en toda la base usando collectionGroup
          const cgQuery = firestore
            .collectionGroup('EspaciosAsignadosOT')
            .where(firebase.firestore.FieldPath.documentId(), '==', espacioId)
            .limit(1);

          const cgSnap = await cgQuery.get();
          console.log(`[PendingCalculator] 📊 Resultados collectionGroup(EspaciosAsignadosOT) por espacioId=${espacioId}: ${cgSnap.size}`);

          if (!cgSnap.empty) {
            const espacioDoc = cgSnap.docs[0];
            const resolvedChecklistRef = espacioDoc.ref.parent.parent!; // .../ChecklistsOT/{checklistId}

            // Logs de validación de pertenencia
            const parts = resolvedChecklistRef.path.split('/');
            const resolvedEmpresaId = parts[1];
            const resolvedProjectId = parts[3];
            const resolvedChecklistId = resolvedChecklistRef.id;

            console.warn(`[PendingCalculator] ✅ Checklist resuelto por collectionGroup: ${resolvedChecklistId} (ruta: ${resolvedChecklistRef.path})`);
            if (resolvedEmpresaId !== empresaId || resolvedProjectId !== projectId) {
              console.warn(`[PendingCalculator] ⚠️ IDs recibidos (empresa:${empresaId}, proyecto:${projectId}) no coinciden con resueltos (empresa:${resolvedEmpresaId}, proyecto:${resolvedProjectId}). Se usará la ruta resuelta igualmente.`);
            }

            effectiveChecklistRef = resolvedChecklistRef;
          } else {
            console.warn(`[PendingCalculator] ⚠️ No se encontró el espacio ${espacioId} en collectionGroup. Abortando actualización.`);
            return;
          }
        } catch (cgError) {
          console.warn(`[PendingCalculator] ❌ Error en fallback collectionGroup:`, cgError);
          return;
        }
      }
    }

    console.log(`[PendingCalculator] ✅ Checklist existe o fue resuelto - continuando con cálculo`);

    console.log(`[PendingCalculator] ===== INICIANDO CÁLCULO POR RUTA DEL CHECKLIST =====`);

    // Calcular pendientes recorriendo espacios asignados del checklist para evitar falsos positivos
    const checklistResult = await calculatePendingItemsForChecklist(effectiveChecklistRef);

    // Calcular y actualizar el espacio afectado (si existe)
    try {
      const espacioRef = effectiveChecklistRef.collection('EspaciosAsignadosOT').doc(espacioId);
      const espacioDoc = await espacioRef.get();
      if (!espacioDoc.exists) {
        console.warn(`[PendingCalculator] ⚠️ El espacio ${espacioId} no existe bajo el checklist ${checklistId}. Se omitirá actualización del espacio.`);
      } else {
        const espacioResult = await calculatePendingItemsForEspacio(espacioRef);
        const updateDataEspacio = {
          hasPendingItems: espacioResult.hasPending,
          pendingResponsesCount: espacioResult.count,
          lastPendingCheck: firebase.firestore.FieldValue.serverTimestamp()
        };
        console.log(`[PendingCalculator] DATOS A GUARDAR EN ESPACIO (${espacioRef.path}):`, updateDataEspacio);
        await espacioRef.update(updateDataEspacio);
        console.log(`[PendingCalculator] ✅ ESPACIO ACTUALIZADO: ${espacioRef.path}`);
      }
    } catch (espacioError) {
      console.warn(`[PendingCalculator] Error actualizando el espacio ${espacioId}:`, espacioError);
    }

    // 3. Actualizar el documento del Checklist con los campos requeridos
    const updateDataChecklist = {
      hasPendingItems: checklistResult.hasPending,
      pendingItemsCount: checklistResult.count,
      lastPendingCheck: firebase.firestore.FieldValue.serverTimestamp()
    };
    console.log(`[PendingCalculator] DATOS A GUARDAR EN CHECKLIST:`, updateDataChecklist);

    await effectiveChecklistRef.update(updateDataChecklist);
    console.log(`[PendingCalculator] ✅ DOCUMENTO CHECKLIST ACTUALIZADO EXITOSAMENTE en: ${effectiveChecklistRef.path}`);

    console.log(`[PendingCalculator] 📊 RESUMEN: Checklist ${checklistId} ahora tiene ${checklistResult.count} items pendientes`);

  } catch (error) {
    console.error(`[PendingCalculator] Error actualizando estado pendiente:`, error);
    throw error;
  }
};

/**
 * Función para recalcular todos los campos de items pendientes en un proyecto
 * Útil para migración de datos existentes
 */
export const recalculateAllPendingItems = async (
  empresaId: string,
  projectId: string
) => {
  console.log(`[PendingCalculator] Recalculando todos los items pendientes para proyecto ${projectId}`);

  try {
    const projectRef = firestore
      .collection('Empresas').doc(empresaId)
      .collection('ProyectosOT').doc(projectId);

    // Obtener todos los checklists del proyecto
    const checklistsSnapshot = await projectRef.collection('ChecklistsOT').get();

    for (const checklistDoc of checklistsSnapshot.docs) {
      const checklistRef = checklistDoc.ref;

      // Obtener todos los espacios del checklist
      const espaciosSnapshot = await checklistRef.collection('EspaciosAsignadosOT').get();

      for (const espacioDoc of espaciosSnapshot.docs) {
        const espacioResult = await calculatePendingItemsForEspacio(espacioDoc.ref);

        // Actualizar espacio
        await espacioDoc.ref.update({
          hasPendingItems: espacioResult.hasPending,
          lastPendingCheck: firebase.firestore.FieldValue.serverTimestamp(),
          pendingResponsesCount: espacioResult.count
        });
      }

      // Recalcular checklist
      const checklistResult = await calculatePendingItemsForChecklist(checklistRef);

      // Actualizar checklist
      await checklistRef.update({
        hasPendingItems: checklistResult.hasPending,
        lastPendingCheck: firebase.firestore.FieldValue.serverTimestamp(),
        pendingItemsCount: checklistResult.count
      });
    }

    console.log(`[PendingCalculator] Recálculo completado para proyecto ${projectId}`);
  } catch (error) {
    console.error(`[PendingCalculator] Error en recálculo masivo:`, error);
    throw error;
  }
};

/**
 * Función utilitaria para recalcular campos pendientes después de cambiar estado de respuestas individuales
 * Se puede llamar desde cualquier componente o acción que modifique el estado de respuestas
 */
export const recalculatePendingAfterAnswerChange = async (
  sheetRef: firebase.firestore.DocumentReference
) => {
  console.log('[PendingCalculator] Iniciando recálculo después de cambio en respuesta individual');

  try {
    // Extraer IDs de la ruta del documento
    const pathParts = sheetRef.path.split('/');
    const empresaId = pathParts[1];
    const projectId = pathParts[3];
    const checklistId = pathParts[5];
    const espacioId = pathParts[7];

    console.log(`[PendingCalculator] IDs extraídos - empresa: ${empresaId}, proyecto: ${projectId}, checklist: ${checklistId}, espacio: ${espacioId}`);

    await updatePendingStatusAfterChange(
      empresaId,
      projectId,
      checklistId,
      espacioId
    );

    console.log('[PendingCalculator] Recálculo completado después de cambio en respuesta');
  } catch (error) {
    console.error('[PendingCalculator] Error en recálculo después de cambio en respuesta:', error);
    throw error;
  }
};

/**
 * Cloud Function trigger para recalcular items pendientes cuando se modifican respuestas
 * Esta función debe ser llamada desde las Cloud Functions que actualizan respuestas
 */
export const triggerPendingRecalculation = async (
  empresaId: string,
  projectId: string,
  checklistId: string,
  espacioId: string
) => {
  console.log(`[PendingCalculator] Trigger de recálculo desde Cloud Function - empresa: ${empresaId}, proyecto: ${projectId}, checklist: ${checklistId}, espacio: ${espacioId}`);

  try {
    await updatePendingStatusAfterChange(
      empresaId,
      projectId,
      checklistId,
      espacioId
    );

    console.log('[PendingCalculator] Recálculo completado desde Cloud Function trigger');
    return { success: true };
  } catch (error) {
    console.error('[PendingCalculator] Error en recálculo desde Cloud Function trigger:', error);
    return { success: false, error: error.message };
  }
};
